import IMask from 'imask';
import moment from 'moment-timezone';
import { RcFile } from 'antd/lib/upload';
import { makeStyles } from '@mui/styles';
import { Gutter } from 'antd/lib/grid/row';
import Checkbox from '@mui/material/Checkbox';
import React, { useEffect, useState } from 'react';
import { Row, Col, Input, Select, InputNumber, DatePicker } from 'antd';

import { filterOption } from '@utils';
import { PolicyFarms } from './PolicyFarms';
import { CxOptions, s3http } from '@network';
import { CropDocuments } from './CropDocuments';
import { Credit, CreditCrop, CxFarm } from '@types';
import { FilePreview, FileUpload, InitialRow } from '@components';
import { PhoneControlledInput } from '@components/inputs/PhoneControlledInput';

const DATE_FORMAT = 'DD/MM/YYYY';
const DATE_FORMAT_DB = 'YYYY-MM-DD';
const MASKED = IMask.createMask({
  blocks: {
    DD: { from: 1, mask: IMask.MaskedRange, to: 31 },
    MM: { from: 1, mask: IMask.MaskedRange, to: 12 },
    YYYY: { from: 1900, mask: IMask.MaskedRange, to: new Date().getFullYear() },
  },
  format: (date: Date) => moment(date).format(DATE_FORMAT),
  mask: Date,
  parse: (date: string) => moment(date, DATE_FORMAT),
  pattern: DATE_FORMAT,
});

interface Props {
  insurance: CreditCrop;
  credit: Credit;
  updateInsurance: (insurance: CreditCrop) => void;
  cxOptions: CxOptions;
  creditFarms: CxFarm[];
  setCreditFarms: (farms: CxFarm[]) => void;
  usedFarmIds: number[];
  idx: number;
  isDownloading?: boolean;
  shortEdit?: boolean;
  editable?: boolean;
}

export const PolicyInsuranceForm = (props: Props) => {
  const { insurance, updateInsurance, shortEdit } = props;
  const cls = useStyles();

  return (
    <div className={cls.container}>
      <div className={cls.cultivo}>
        <Checkbox
          checked={insurance.isProtectedCultivation}
          disabled={shortEdit}
          onChange={e => updateInsurance({
            ...insurance,
            isProtectedCultivation: e.target.checked,
          })}
          size="medium"
        />
        <span>Cultivo Protegido</span>
      </div>

      {insurance.isProtectedCultivation ? (
        <PolicyProtectedForm {...props} />
      ) : (
        <PolicyUnprotectedForm {...props} />
      )}
    </div>
  );
};

const PolicyUnprotectedForm = (props: Props) => {
  const cls = useStyles();
  const printRef = React.useRef<any>();
  const [varieties, setVarieties] = useState<string[]>([]);

  const { insurance, credit, updateInsurance, cxOptions, idx, shortEdit, editable } = props;
  const documents = insurance.creditCropDocuments || [];
  const rowGutter = [30, 0] as [Gutter, Gutter];
  const isCompany = credit.accredited === 'company';

  useEffect(() => {
    if (!insurance.financedCropId) return;
    const crop = cxOptions.crops.find(c => c.id === insurance.financedCropId);
    if (!crop) return;
    setVarieties(crop.varietiesNames || []);
  }, [insurance.financedCropId, cxOptions]);

  useEffect(() => {
    if ((cxOptions.crops || []).length > 0 && insurance.financedCropId) {
      updateInsurance({ ...insurance, cropVariety: null });
    }
  }, [insurance.financedCropId]);

  return (
    <div>
      <Row gutter={[35, 10]}>
        <Col sm={14} className={cls.fields}>
          <div className={cls.isDownloading} ref={printRef}>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Expediente:" labelSize={7}>
                  <Input
                    className={cls.formInput}
                    value={credit.code}
                    disabled={!editable || shortEdit} />
                </InitialRow>
              </Col>
              <Col sm={12}>
                <InitialRow title="Acreditado:" labelSize={7}>
                  <Input
                    className={cls.formInput}
                    disabled={!editable || shortEdit}
                    value={insurance.accreditedName}
                    onChange={e => updateInsurance({
                      ...insurance,
                      accreditedName: e.target.value,
                    })}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Teléfono:" labelSize={7}>
                  <PhoneControlledInput
                    className={cls.formInput}
                    value={insurance.phone || ''}
                    disabled={!editable || shortEdit}
                    onChange={value => updateInsurance({
                      ...insurance,
                      phone: value,
                    })}
                  />
                </InitialRow>
              </Col>
              <Col sm={12}>
                <InitialRow title="Rep. Legal:" labelSize={7}>
                  <Input
                    className={cls.formInput}
                    value={insurance.repLegal || ''}
                    onChange={e => updateInsurance({
                      ...insurance,
                      repLegal: e.target.value,
                    })}
                    disabled={!editable || !isCompany || shortEdit}
                    title={!isCompany ? 'Sólo para personas morales' : undefined}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Cultivo*:" labelSize={7}>
                  <Select
                    onChange={financedCropId => updateInsurance({ ...insurance, financedCropId })}
                    disabled={!editable || shortEdit}
                    value={insurance.financedCropId}
                    className={cls.select}
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={cxOptions.crops.map(o => ({ value: o.id, label: o.nameMx }))}
                  />
                </InitialRow>
              </Col>
              <Col sm={12}>
                <InitialRow title="Variedad:" labelSize={7}>
                  <Select
                    onChange={cropVariety => updateInsurance({ ...insurance, cropVariety })}
                    disabled={!editable || shortEdit}
                    value={insurance.cropVariety || ''}
                    className={cls.select}
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={varieties.map(variety => ({ value: variety, label: variety }))}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Municipio:" labelSize={7}>
                  <Input
                    className={cls.formInput}
                    disabled={!editable || shortEdit}
                    value={insurance.municipality}
                    onChange={e => updateInsurance({ ...insurance, municipality: e.target.value })}
                  />
                </InitialRow>
              </Col>
              <Col sm={12}>
                <InitialRow title="Estado:" labelSize={7}>
                  <Select
                    onChange={stateId => updateInsurance({ ...insurance, stateId })}
                    disabled={!editable || shortEdit}
                    value={insurance.stateId}
                    className={cls.select}
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={cxOptions.states.map(o => ({ value: o.id, label: o.name }))}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Contacto Campo:" labelSize={9}>
                  <Input
                    className={cls.formInput}
                    value={insurance.contactName || ''}
                    disabled={!editable || shortEdit}
                    onChange={e => updateInsurance({ ...insurance, contactName: e.target.value })}
                  />
                </InitialRow>
              </Col>
              <Col sm={12}>
                <InitialRow title="Tel. Contacto:" labelSize={9}>
                  <PhoneControlledInput
                    className={cls.formInput}
                    disabled={!editable || shortEdit}
                    value={insurance.contactPhone || ''}
                    onChange={value => updateInsurance({ ...insurance, contactPhone: value })}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={24}>
                <InitialRow title="Ubicación:" labelSize={6}>
                  <PolicyFarms {...props} isDownloading={false} />
                </InitialRow>
              </Col>
            </Row>
            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Superficie total:" labelSize={12}>
                  <InputNumber
                    min={0}
                    disabled={!editable || shortEdit}
                    max={1000000000}
                    maxLength={13}
                    prefix={<div className={cls.inputPrefix}>Ha</div>}
                    value={insurance.financedHaCx}
                    onChange={val => updateInsurance({ ...insurance, financedHaCx: val || 0 })}
                    className={cls.formInput}
                    bordered={false}
                    controls={false}
                  />
                </InitialRow>
              </Col>
              <Col sm={12}>
                <InitialRow title="Rendimiento:" labelSize={12}>
                  <InputNumber
                    min={0}
                    disabled={!editable || shortEdit}
                    max={1000000000}
                    maxLength={13}
                    prefix={<div className={cls.inputPrefix}>Tons/Ha</div>}
                    value={insurance.expectedWeightCx}
                    onChange={val => updateInsurance({ ...insurance, expectedWeightCx: val || 0 })}
                    className={cls.formInput}
                    bordered={false}
                    controls={false}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Fecha de Siembra:" labelSize={12}>
                  <DatePicker
                    placeholder="DD/MM/YYYY"
                    disabled={!editable || shortEdit}
                    className={cls.formInput}
                    suffixIcon={null}
                    allowClear={true}
                    value={insurance.sawingDate ? moment(insurance.sawingDate) : null}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      const input = event.target as HTMLInputElement;
                      if (!input.value) {
                        return updateInsurance({ ...insurance, sawingDate: null });
                      }
                      input.value = MASKED.resolve(input.value);
                      setTimeout(() => {
                        const momentDate = moment(input.value, DATE_FORMAT, true);
                        momentDate.isValid() && updateInsurance({
                          ...insurance,
                          sawingDate: momentDate.format(DATE_FORMAT_DB),
                        });
                      }, 200);
                    }}
                    onChange={selectedValue => {
                      if (!selectedValue) {
                        return updateInsurance({ ...insurance, sawingDate: null });
                      }
                      const momentDate = moment(selectedValue, DATE_FORMAT, true);
                      momentDate.isValid() && updateInsurance({
                        ...insurance,
                        sawingDate: momentDate.format(DATE_FORMAT_DB),
                      });
                    }}
                    picker="date"
                    format={DATE_FORMAT}
                  />
                </InitialRow>
              </Col>
              <Col md={12}>
                <InitialRow title="Cosecha Escalonada:" labelSize={12}>
                  <Checkbox
                    disabled={!editable || shortEdit}
                    checked={insurance.harvestStepwise}
                    onChange={e => updateInsurance({
                      ...insurance,
                      harvestStepwise: e.target.checked,
                    })}
                    size="medium"
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Fecha de Cosecha:" labelSize={12}>
                  <DatePicker
                    placeholder="DD/MM/YYYY"
                    disabled={!editable || shortEdit}
                    className={cls.formInput}
                    suffixIcon={null}
                    allowClear={true}
                    value={insurance.harvestDate ? moment(insurance.harvestDate) : null}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      const input = event.target as HTMLInputElement;
                      if (!input.value) {
                        return updateInsurance({ ...insurance, harvestDate: null });
                      }
                      input.value = MASKED.resolve(input.value);
                      setTimeout(() => {
                        const momentDate = moment(input.value, DATE_FORMAT, true);
                        momentDate.isValid() && updateInsurance({
                          ...insurance,
                          harvestDate: momentDate.format(DATE_FORMAT_DB),
                        });
                      }, 200);
                    }}
                    onChange={selectedValue => {
                      if (!selectedValue) {
                        return updateInsurance({ ...insurance, harvestDate: null });
                      }
                      const momentDate = moment(selectedValue, DATE_FORMAT, true);
                      momentDate.isValid() && updateInsurance({
                        ...insurance,
                        harvestDate: momentDate.format(DATE_FORMAT_DB),
                      });
                    }}
                    picker="date"
                    format={DATE_FORMAT}
                  />
                </InitialRow>
              </Col>
              <Col md={12}>
                <InitialRow title="Ciclo Agrícola:" labelSize={9}>
                  <Select
                    onChange={val => updateInsurance({ ...insurance, agroCycle: val })}
                    disabled={!editable || shortEdit}
                    value={insurance.agroCycle}
                    className={cls.select}
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={[
                      { value: 'OI', label: 'OI' },
                      { value: 'PV', label: 'PV' },
                      { value: 'Perene', label: 'Perene' },
                    ]}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={12}>
                <InitialRow title="Tecnificación:" labelSize={9}>
                  <Input
                    className={cls.formInput}
                    value={insurance.documentation || ''}
                    readOnly={!editable}
                    onChange={e => updateInsurance({ ...insurance, documentation: e.target.value })}
                  />
                </InitialRow>
              </Col>
              <Col sm={12}>
                <InitialRow title="Datos del ciclo:" labelSize={9}>
                  <Input
                    className={cls.formInput}
                    value={insurance.cycleData || ''}
                    readOnly={!editable}
                    onChange={e => updateInsurance({ ...insurance, cycleData: e.target.value })}
                  />
                </InitialRow>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col sm={24}>
                <InitialRow title="Información extra:" labelSize={9}>
                  <Input.TextArea
                    className={cls.formInput}
                    value={insurance.extraInfo || ''}
                    readOnly={!editable}
                    onChange={e => updateInsurance({ ...insurance, extraInfo: e.target.value })}
                  />
                </InitialRow>
              </Col>
            </Row>
          </div>
        </Col>

        <Col sm={10}>
          <CropDocuments
            policyIdx={idx}
            documents={documents}
            insurance={insurance}
            updateInsurance={updateInsurance}
            credit={credit}
            disabled={!editable || shortEdit}
          />
        </Col>
      </Row>

      <div className={cls.dates}>
        <Row gutter={rowGutter}>
          <Col sm={8}>
            <InitialRow title="Solicitado el:" labelSize={12}>
              <DatePicker
                placeholder="DD/MM/YYYY"
                disabled={!editable || shortEdit}
                className={cls.formInput}
                suffixIcon={null}
                allowClear={true}
                value={insurance.requestedAt ? moment(insurance.requestedAt) : null}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  const input = event.target as HTMLInputElement;
                  if (!input.value) {
                    return updateInsurance({ ...insurance, requestedAt: null });
                  }
                  input.value = MASKED.resolve(input.value);
                  setTimeout(() => {
                    const momentDate = moment(input.value, DATE_FORMAT, true);
                    momentDate.isValid() && updateInsurance({
                      ...insurance,
                      requestedAt: momentDate.format(DATE_FORMAT_DB),
                    });
                  }, 200);
                }}
                onChange={selectedValue => {
                  if (!selectedValue) {
                    return updateInsurance({ ...insurance, requestedAt: null });
                  }
                  const momentDate = moment(selectedValue, DATE_FORMAT, true);
                  momentDate.isValid() && updateInsurance({
                    ...insurance,
                    requestedAt: momentDate.format(DATE_FORMAT_DB),
                  });
                }}
                picker="date"
                format={DATE_FORMAT}
              />
            </InitialRow>
          </Col>
          <Col sm={8}>
            <InitialRow title="Cotizado el:" labelSize={12}>
              <DatePicker
                placeholder="DD/MM/YYYY"
                disabled={!editable || shortEdit}
                className={cls.formInput}
                suffixIcon={null}
                allowClear={true}
                value={insurance.quotedAt ? moment(insurance.quotedAt) : null}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  const input = event.target as HTMLInputElement;
                  if (!input.value) {
                    return updateInsurance({ ...insurance, quotedAt: null });
                  }
                  input.value = MASKED.resolve(input.value);
                  setTimeout(() => {
                    const momentDate = moment(input.value, DATE_FORMAT, true);
                    momentDate.isValid() && updateInsurance({
                      ...insurance,
                      quotedAt: momentDate.format(DATE_FORMAT_DB),
                    });
                  }, 200);
                }}
                onChange={selectedValue => {
                  if (!selectedValue) {
                    return updateInsurance({ ...insurance, quotedAt: null });
                  }
                  const momentDate = moment(selectedValue, DATE_FORMAT, true);
                  momentDate.isValid() && updateInsurance({
                    ...insurance,
                    quotedAt: momentDate.format(DATE_FORMAT_DB),
                  });
                }}
                picker="date"
                format={DATE_FORMAT}
              />
            </InitialRow>
          </Col>
          <Col sm={8}>
            <InitialRow title="Póliza Generada el:" labelSize={12}>
              <DatePicker
                placeholder="DD/MM/YYYY"
                disabled={!editable || shortEdit}
                className={cls.formInput}
                suffixIcon={null}
                allowClear={true}
                value={insurance.policyGeneratedAt ? moment(insurance.policyGeneratedAt) : null}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  const input = event.target as HTMLInputElement;
                  if (!input.value) {
                    return updateInsurance({ ...insurance, policyGeneratedAt: null });
                  }
                  input.value = MASKED.resolve(input.value);
                  setTimeout(() => {
                    const momentDate = moment(input.value, DATE_FORMAT, true);
                    momentDate.isValid() && updateInsurance({
                      ...insurance,
                      policyGeneratedAt: momentDate.format(DATE_FORMAT_DB),
                    });
                  }, 200);
                }}
                onChange={selectedValue => {
                  if (!selectedValue) {
                    return updateInsurance({ ...insurance, policyGeneratedAt: null });
                  }
                  const momentDate = moment(selectedValue, DATE_FORMAT, true);
                  momentDate.isValid() && updateInsurance({
                    ...insurance,
                    policyGeneratedAt: momentDate.format(DATE_FORMAT_DB),
                  });
                }}
                picker="date"
                format={DATE_FORMAT}
              />
            </InitialRow>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const PolicyProtectedForm = (props: Props) => {
  const { insurance, credit, updateInsurance, idx } = props;
  const documents = insurance.creditCropDocuments || [];
  const cls = useStyles();

  const uploadQuotationFile = async (rcFile: RcFile): Promise<string> => {
    const file = await s3http.uploadFile(rcFile as File);
    updateInsurance({ ...insurance, quotationFile: file });
    return file.adminUrl;
  };

  const downloadQuoteDoc = () => {
    const link = document.createElement('a');
    const fileName = 'Covered_Crop_Quote_Insurance.xlsx';
    const fileUrl = `https://verqor-public.s3.us-west-2.amazonaws.com/${fileName}`;

    if (typeof link.download === 'string') {
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(fileUrl, '_blank');
    }
  };

  return (
    <>
      <div className={cls.protected}>
        <div className={cls.protectedText}>
          <span>Para cultivo protegido, completa</span>
          <span className={cls.protectedLink} onClick={downloadQuoteDoc}>este documento</span>
          <span>y súbelo aquí al terminar.</span>
        </div>
      </div>
      <Row gutter={[35, 10]}>
        <Col sm={3}>
          <div className={cls.document}>
            <div className={cls.documentTitle}>Quotation</div>
            {insurance.quotationFile ? (
              <FilePreview file={insurance.quotationFile} wrapClass={cls.preview}
                downloadFilename={`Quotation ${credit.code} #${idx + 1}`} />
            ) : (
              <FileUpload uploadAction={uploadQuotationFile}
                type="pdf+xlsx" className={cls.upload} />
            )}
          </div>
        </Col>
        <Col sm={11}>
          <div className={cls.fields}>
            <div className={cls.fieldsLabel}>Ubicación:</div>
            <PolicyFarms {...props} />
          </div>
        </Col>
        <Col sm={10}>
          <CropDocuments
            policyIdx={idx}
            documents={documents}
            insurance={insurance}
            updateInsurance={updateInsurance}
            credit={credit}
          />
        </Col>
      </Row>
    </>
  );
};

const useStyles = makeStyles({
  container: {},
  protected: {
    margin: '20px 20px 15px',
  },
  protectedText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 15,
    borderBottom: '1px solid #042E6B',
    fontSize: '14px',
    fontWeight: 600,
  },
  protectedLink: {
    color: '#1890FF',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    margin: '0 6px',
  },
  cultivo: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 20px',
    margin: '0 0 -30px',
  },
  isDownloading: {
    padding: '10px 15px',
  },
  fields: {
    marginBottom: 15,
    '& .ant-row': {
      marginTop: 5,
      minHeight: 32,
    },
    '& .ir-label': {
      fontSize: 14,
    },
  },
  fieldsLabel: {
    fontSize: 16,
    color: '#000',
  },
  formInput: {
    border: 'none',
    borderRadius: 0,
    borderBottom: '1px solid var(--logo-color)',
    width: '100%',
    fontSize: '12px !important',
    backgroundColor: 'var(--foreground-color)',
    color: 'var(--text-color)',
    '&:focus, &:hover, &[class~="ant-input-number-focused"], & [class~="ant-input-number-focused"], &.ant-input-number-affix-wrapper-focused': {
      boxShadow: 'none',
      borderBottom: '1px solid var(--primary-color) !important',
      '& .ant-input-number-input-wrap': {
        border: 'none !important',
      },
    },
    '& .ant-picker-input': {
      border: 'none !important',
    },
    '& input': {
      fontSize: '12px !important',
    },
    '&:disabled': {
      backgroundColor: 'transparent',
    },
    '&[class*="ant-input-number-disabled"]': {
      backgroundColor: 'transparent',
    },
    '&[class*="ant-picker-disabled"]': {
      backgroundColor: 'transparent',
    },
  },
  select: {
    width: '100%',
    border: 'none !important',
    backgroundColor: 'transparent !important',
    '& .ant-select-selector': {
      backgroundColor: 'transparent !important',
      border: 'none !important',
      borderBottom: '1px solid rgba(0, 42, 119, 0.10) !important',
      borderRadius: '0 !important',
    },
  },
  inputPrefix: {
    position: 'absolute',
    top: 6,
    right: 15,
  },
  upload: {
    width: 128,
    height: 164,
  },
  preview: {
    width: 128,
    height: 164,
    border: '2px solid #042E6B',
    borderRadius: '10px',
    background: 'var(--foreground-color)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  document: {
    marginRight: 15,
    marginBottom: 15,
    position: 'relative',
  },
  documentTitle: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1,
    color: 'var(--title-color)',
    marginBottom: 4,
    textAlign: 'center',
    paddingBottom: 10,
  },
  dates: {
    padding: '10px 15px',
    marginBottom: 40,
  },
});
